// Client-side fallback for ingredient measure hints in Cooking Mode.
//
// The recipe-details LLM now provides a localized `measure_hint` per ingredient
// in `ingredient_storage`. For older saved recipes (generated before that field
// existed) we derive a simple HOUSEHOLD-measure hint (cups / tbsp / tsp) on the
// client so users can still eyeball weights and volumes. The output is English
// (e.g. "≈ 3 cups") and is localized at the render site via translateQty().
//
// We intentionally only convert:
//   - metric VOLUME (ml, l, cl, dl) — always convertible to household measures
//   - metric WEIGHT (g, kg) — only when we know the ingredient's density
// Household-unit sources (cup/tbsp/tsp), counts (pieces/cloves/slices) and
// "to taste" never get a hint (the displayed quantity already reads naturally,
// or a volume conversion would be meaningless).

import { FOOD_DENSITIES } from './groceryConstants';

const CUP_ML = 240;
const TBSP_ML = 15;
const TSP_ML = 5;

const UNICODE_FRACTIONS = {
  '½': 0.5,
  '¼': 0.25,
  '¾': 0.75,
  '⅓': 1 / 3,
  '⅔': 2 / 3,
  '⅛': 0.125,
};

const WEIGHT_UNITS = new Set(['g', 'gram', 'grams', 'gr', 'kg', 'kilogram', 'kilograms']);
const VOLUME_UNITS = new Set([
  'ml', 'milliliter', 'milliliters', 'millilitre', 'millilitres',
  'l', 'liter', 'liters', 'litre', 'litres', 'cl', 'dl',
]);
const HOUSEHOLD_UNITS = new Set([
  'cup', 'cups', 'tbsp', 'tablespoon', 'tablespoons', 'tsp', 'teaspoon', 'teaspoons',
]);

// Parse a quantity string like "120 g", "1.5 kg", "700ml", "1/2 cup", "2 cloves".
const parseQuantity = (raw) => {
  if (raw == null) return null;
  let s = String(raw).toLowerCase().trim();
  if (!s) return null;

  // Normalize unicode fractions to a decimal token (" 0.5", " 0.25", ...).
  for (const [glyph, val] of Object.entries(UNICODE_FRACTIONS)) {
    s = s.split(glyph).join(` ${val}`);
  }
  s = s.replace(/\s+/g, ' ').trim();

  let value = null;
  let rest = s;
  let m;
  if ((m = s.match(/^(\d+)\s+(\d+)\/(\d+)/))) {
    // Mixed number: "1 1/2"
    value = parseInt(m[1], 10) + parseInt(m[2], 10) / parseInt(m[3], 10);
    rest = s.slice(m[0].length);
  } else if ((m = s.match(/^(\d+)\/(\d+)/))) {
    // Simple fraction: "1/2"
    value = parseInt(m[1], 10) / parseInt(m[2], 10);
    rest = s.slice(m[0].length);
  } else if ((m = s.match(/^(\d+(?:\.\d+)?)/))) {
    // Decimal/integer: "200", "1.5"
    value = parseFloat(m[1]);
    rest = s.slice(m[0].length);
  }
  if (value == null || !isFinite(value)) return null;

  // First alphabetic token after the number is the unit.
  const unitMatch = rest.match(/[a-z]+/i);
  const unit = unitMatch ? unitMatch[0].toLowerCase() : '';

  let kind = 'other';
  if (WEIGHT_UNITS.has(unit)) kind = 'weight';
  else if (VOLUME_UNITS.has(unit)) kind = 'volume';
  else if (HOUSEHOLD_UNITS.has(unit)) kind = 'household';

  return { value, unit, kind };
};

// True only for metric weight/volume quantities (the ones worth hinting).
export const isWeightOrVolumeQty = (raw) => {
  const p = parseQuantity(raw);
  return !!p && (p.kind === 'weight' || p.kind === 'volume');
};

const toGrams = (value, unit) => {
  if (unit === 'kg' || unit === 'kilogram' || unit === 'kilograms') return value * 1000;
  return value; // g / gram / grams / gr
};

const toMl = (value, unit) => {
  switch (unit) {
    case 'l':
    case 'liter':
    case 'liters':
    case 'litre':
    case 'litres':
      return value * 1000;
    case 'cl':
      return value * 10;
    case 'dl':
      return value * 100;
    default:
      return value; // ml and spelled-out variants
  }
};

const lookupDensity = (name) => {
  if (!name) return null;
  const n = String(name).toLowerCase().trim();
  if (!n) return null;
  if (FOOD_DENSITIES[n] != null) return FOOD_DENSITIES[n];
  // Longest key first so "olive oil" wins over "oil", etc.
  const keys = Object.keys(FOOD_DENSITIES).sort((a, b) => b.length - a.length);
  for (const k of keys) {
    if (n.includes(k)) return FOOD_DENSITIES[k];
  }
  return null;
};

// Round to the nearest `step` (e.g. 0.25 for cups, 0.5 for spoons).
const roundTo = (n, step) => Math.round(n / step) * step;

const FRACTION_GLYPH = { 0.25: '¼', 0.5: '½', 0.75: '¾' };

const formatNumber = (n) => {
  const whole = Math.floor(n + 1e-9);
  const frac = +(n - whole).toFixed(2);
  const glyph = FRACTION_GLYPH[frac];
  if (whole === 0) return glyph || String(+n.toFixed(2));
  if (glyph) return `${whole}${glyph}`;
  return String(whole);
};

// Convert a volume in ml into an English household-measure hint string.
const householdFromMl = (ml) => {
  if (!isFinite(ml) || ml < 2.5) return null;
  if (ml >= 180) {
    const cups = roundTo(ml / CUP_ML, 0.25);
    if (cups < 0.25) return null;
    const word = cups > 1 ? 'cups' : 'cup';
    return `≈ ${formatNumber(cups)} ${word}`;
  }
  if (ml >= 10) {
    const tbsp = Math.max(0.5, roundTo(ml / TBSP_ML, 0.5));
    return `≈ ${formatNumber(tbsp)} tbsp`;
  }
  const tsp = Math.max(0.5, roundTo(ml / TSP_ML, 0.5));
  return `≈ ${formatNumber(tsp)} tsp`;
};

// Public: derive an English household-measure hint, or null when not applicable.
// `ingredientName` is only needed for weight→volume density conversion.
export const getMeasureHint = (quantityString, ingredientName) => {
  const p = parseQuantity(quantityString);
  if (!p) return null;
  if (p.kind === 'volume') {
    return householdFromMl(toMl(p.value, p.unit));
  }
  if (p.kind === 'weight') {
    const density = lookupDensity(ingredientName);
    if (!density || density <= 0) return null;
    const grams = toGrams(p.value, p.unit);
    return householdFromMl(grams / density);
  }
  return null;
};

export default getMeasureHint;
